import { DownloadSimpleIcon } from '@phosphor-icons/react'
import { Fragment } from 'react'
import { type CodingGuidelinesResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { TooltipWithHover } from '../tooltip.tsx'
import { formatFileSize, getDocType, getDocTypeBadgeClass } from './coding-guidelines-types.ts'
import { DocTypeIcon } from './doc-type-icon.tsx'
import * as styles from './document-card.css.ts'

type DocumentCardProps = {
  document: CodingGuidelinesResponse
  /** `undefined` for a user who may not edit: the row is then read-only, keeping only Download. */
  onEdit: ((document: CodingGuidelinesResponse) => unknown) | undefined
}

export const DocumentCard: React.FC<DocumentCardProps> = ({ document, onEdit }) => {
  const uploadedAt = document.uploaded_at
    ? formatDate({ date: document.uploaded_at, type: 'mm/dd/yyyy-hh:mm' })
    : undefined
  const uploadedBy = document.uploaded_by
  const sizeLabel = document.size ? formatFileSize(document.size) : undefined
  const downloadHref = document._links?.content?.href

  const subParts: string[] = []
  if (document.filename) {
    subParts.push(document.filename)
  }
  if (sizeLabel) {
    subParts.push(sizeLabel)
  }
  if (uploadedAt && uploadedBy) {
    subParts.push(`Updated ${uploadedAt} by @${uploadedBy}`)
  } else if (uploadedAt) {
    subParts.push(`Updated ${uploadedAt}`)
  }

  const rowContent = (
    <>
      <DocumentTypeBadge filename={document.filename} />
      <div className={styles.meta}>
        <p className={styles.documentName}>{document.display_name}</p>
        {subParts.length > 0 ? (
          <p className={styles.documentSub}>
            {subParts.map((part, i) => (
              <Fragment key={part}>
                {i > 0 ? (
                  <span className={styles.documentSubSep} aria-hidden>
                    ·
                  </span>
                ) : null}
                <span>{part}</span>
              </Fragment>
            ))}
          </p>
        ) : null}
      </div>
      <div />
      <div className={styles.rowActions}>
        {downloadHref ? (
          <TooltipWithHover
            trigger={
              <a
                href={downloadHref}
                download
                className={styles.iconBtn}
                aria-label="Download"
                onClick={e => e.stopPropagation()}
              >
                <DownloadSimpleIcon size={18} weight="light" />
              </a>
            }
          >
            Download
          </TooltipWithHover>
        ) : null}
      </div>
    </>
  )

  return onEdit ? (
    <EditableRow document={document} onEdit={onEdit}>
      {rowContent}
    </EditableRow>
  ) : (
    <ReadOnlyRow>{rowContent}</ReadOnlyRow>
  )
}

type EditableRowProps = React.PropsWithChildren<{
  document: CodingGuidelinesResponse
  onEdit: (document: CodingGuidelinesResponse) => unknown
}>

const EditableRow: React.FC<EditableRowProps> = ({ document, onEdit, children }) => (
  <div
    role="button"
    className={styles.rowEditable}
    onClick={() => onEdit(document)}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onEdit(document)
      }
    }}
    tabIndex={0}
    aria-label={`Edit ${document.display_name ?? 'document'}`}
  >
    {children}
  </div>
)

// Plain content rather than a disabled button: there is nothing to press, and a button that does
// nothing would invite the click.
const ReadOnlyRow: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div role="listitem" className={styles.row}>
    {children}
  </div>
)

const DocumentTypeBadge: React.FC<{ filename: string | undefined }> = ({ filename }) => {
  const docType = filename ? getDocType(filename) : undefined
  const badgeClass = getDocTypeBadgeClass({
    docType,
    classes: {
      base: styles.iconBadge,
      pdf: styles.iconBadgePdf,
      docx: styles.iconBadgeDocx,
      md: styles.iconBadgeMd,
      txt: styles.iconBadgeTxt,
      html: styles.iconBadgeHtml,
    },
  })

  return (
    <span className={badgeClass}>
      <DocTypeIcon docType={docType} size={22} />
    </span>
  )
}
